/**
 * CustomerNotifications — citizen inbox.
 *
 * Wired against the in-app notifications client (`lib/api/notifications.ts`),
 * which derives an inbox view from wallet transactions + mail-item milestones
 * and persists per-notification read state in localStorage. See the docblock
 * on that module for the deferred-backend story.
 */
import { useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Bell,
  CreditCard,
  Filter,
  Loader2,
  MailOpen,
  Package,
  Settings,
  ShieldCheck,
  Truck,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import {
  listMyNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type ListMyNotificationsResponse,
  type NotificationItem,
} from '../../lib/api/notifications';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import { PageHeader } from './PortalUI';

type FilterId = 'all' | 'unread';

const FILTERS: readonly { readonly id: FilterId; readonly label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'unread', label: 'Unread' },
];

function iconFor(item: NotificationItem): JSX.Element {
  if (item.kind === 'wallet') return <CreditCard className="w-4 h-4 text-gray-600" />;
  if (item.kind === 'kyc') return <ShieldCheck className="w-4 h-4 text-gray-600" />;
  if (item.kind === 'milestone') {
    if (/delivered/i.test(item.title)) return <Package className="w-4 h-4 text-gray-600" />;
    return <Truck className="w-4 h-4 text-gray-600" />;
  }
  return <Bell className="w-4 h-4 text-gray-600" />;
}

function dateLabel(iso: string): string {
  const d = new Date(iso);
  const today = new Date();
  if (d.toDateString() === today.toDateString()) return 'Today';
  const y = new Date(today);
  y.setDate(y.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' });
}

function timeLabel(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('en-NG', { hour: '2-digit', minute: '2-digit' });
}

export function CustomerNotifications(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.customerId ?? '';
  const [filter, setFilter] = useState<FilterId>('all');

  const queryKey = ['notifications', 'me', customerId] as const;
  const query = useQuery<ListMyNotificationsResponse, Error>({
    queryKey,
    queryFn: () =>
      listMyNotifications({
        pageSize: 50,
        bearerToken: token ?? undefined,
        customerId,
      }),
    enabled: customerId.length > 0,
    staleTime: 30_000,
  });

  const markReadMutation = useMutation({
    mutationFn: async (id: string) => {
      await markNotificationRead(id, customerId, token ?? undefined);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'me', 'count', customerId] });
    },
  });

  const markAllMutation = useMutation({
    mutationFn: async () => {
      const ids = (query.data?.items ?? []).map((n) => n.id);
      await markAllNotificationsRead(ids, customerId, token ?? undefined);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      void queryClient.invalidateQueries({ queryKey: ['notifications', 'me', 'count', customerId] });
    },
  });

  const items = query.data?.items ?? [];
  const filtered = useMemo(() => {
    if (filter === 'unread') return items.filter((n) => !n.read);
    return items;
  }, [items, filter]);

  const grouped = useMemo(() => {
    const groups: Record<string, NotificationItem[]> = {};
    for (const n of filtered) {
      const key = dateLabel(n.createdAt);
      if (!groups[key]) groups[key] = [];
      groups[key].push(n);
    }
    return groups;
  }, [filtered]);

  const unreadCount = query.data?.unreadCount ?? 0;

  return (
    <div>
      <PageHeader
        title="Notifications"
        description={unreadCount > 0 ? `${unreadCount} unread` : 'You’re all caught up'}
        actions={
          <div className="flex items-center gap-2">
            {unreadCount > 0 ? (
              <button
                type="button"
                onClick={() => markAllMutation.mutate()}
                disabled={markAllMutation.isPending}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-nipost-green border border-nipost-green/40 rounded-lg hover:bg-nipost-green-light disabled:opacity-50"
              >
                <MailOpen className="w-3.5 h-3.5" /> Mark all read
              </button>
            ) : null}
            <Link
              to="/portal/settings"
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-700 border border-border rounded-lg hover:bg-gray-50"
            >
              <Settings className="w-3.5 h-3.5" /> Preferences
            </Link>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <Filter className="w-3.5 h-3.5 text-muted" />
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`px-2.5 py-1 text-[11px] font-semibold rounded-full transition-colors ${
              filter === f.id
                ? 'bg-nipost-green text-white'
                : 'bg-white border border-border text-gray-700 hover:bg-gray-50'
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {query.isLoading ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center">
          <Loader2 className="w-6 h-6 text-muted mx-auto animate-spin" />
          <p className="text-xs text-muted mt-2">Loading notifications…</p>
        </div>
      ) : query.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 flex items-start justify-between gap-3" role="alert">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-red-700 mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-red-900">Couldn&apos;t load notifications</p>
              <p className="text-xs text-red-800 mt-0.5">{query.error.message}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => query.refetch()}
            className="px-3 py-1.5 text-xs font-semibold bg-white border border-red-200 text-red-800 rounded-lg hover:bg-red-50"
          >
            Try again
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-border rounded-xl p-12 text-center">
          <Bell className="w-10 h-10 text-muted mx-auto mb-3" />
          <p className="text-sm font-semibold text-gray-700 mb-1">No notifications</p>
          <p className="text-xs text-muted">
            When something happens with your shipments or wallet, you&apos;ll see it here.
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {Object.entries(grouped).map(([day, list]) => (
            <div key={day}>
              <h3 className="text-[10px] font-bold text-muted uppercase tracking-wider mb-2">
                {day}
              </h3>
              <div className="bg-white border border-border rounded-xl overflow-hidden">
                {list.map((n) => (
                  <div
                    key={n.id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-border last:border-b-0 ${
                      !n.read ? 'bg-nipost-green-light/20' : ''
                    }`}
                  >
                    <div className="w-9 h-9 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                      {iconFor(n)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          to={n.deepLink}
                          onClick={() => markReadMutation.mutate(n.id)}
                          className="block flex-1"
                        >
                          <p className={`text-sm ${!n.read ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                            {n.title}
                          </p>
                          <p className="text-xs text-muted mt-0.5">{n.body}</p>
                        </Link>
                        <span className="text-[10px] text-muted flex-shrink-0">
                          {timeLabel(n.createdAt)}
                        </span>
                      </div>
                      {n.trackingNumber ? (
                        <Link
                          to={n.deepLink}
                          className="inline-flex items-center gap-1 mt-1 text-[10px] font-mono text-nipost-green hover:underline"
                        >
                          {n.trackingNumber}
                        </Link>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-1">
                      {!n.read ? (
                        <button
                          type="button"
                          onClick={() => markReadMutation.mutate(n.id)}
                          className="p-1 rounded hover:bg-gray-100"
                          aria-label={`Mark ${n.title} read`}
                        >
                          <MailOpen className="w-3.5 h-3.5 text-muted" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
